<template>
  <b-modal
    v-model="show"
    centered
    size="md"
    :ok-disabled="isOverMaxLength"
    @show="onShow"
    @ok="onOk"
  >
    <template #modal-title>
      <h3 class="font-weight-bold">Select Options</h3>
    </template>

    <div v-if="isLoading" class="text-center py-5">
      <digger-loader />
    </div>
    <b-row v-if="!isLoading">
      <b-col>
        <b-form-input
          v-model.lazy="searchQuery"
          placeholder="Type to search options"
          @input="onSearchQueryChange"
          class="mb-2"
          autofocus
          clearable
        />
        <div class="text-danger" style="height: 32px">
          <span v-if="isMaxReached">
            You can only select up to 3 options.
          </span>
        </div>
        <div
          v-if="maxLength"
          id="options-picker-length"
          class="small mb-2"
          :class="isOverMaxLength ? 'text-danger' : 'text-muted'"
        >
          {{ pickedJoined.length }}/{{ maxLength }}
          <span v-if="isOverMaxLength">
            - Too many selections. Remove some (max {{ maxLength }} characters)
          </span>
        </div>
      </b-col>
    </b-row>
    <b-row v-if="!isLoading" class="overflow-auto" style="height: 480px">
      <b-col>
        <div v-for="option in options" :key="option.id">
          <!-- prettier-ignore -->
          <b-form-group v-if="searchQuery === '' || option.value.toLowerCase().includes(searchQuery.toLowerCase()) || pickedOptionIds.includes(option.id)">
              <b-form-checkbox v-model="pickedOptionIds" :value="option.id" :checked="isChecked(option.id)" @change="onCheckChange()">
                {{ option.value }}
              </b-form-checkbox>
            </b-form-group>
        </div>
      </b-col>
    </b-row>
  </b-modal>
</template>

<script>
import { mapActions, mapState } from 'vuex'

export default {
  name: 'OptionsPickerModal',

  props: {
    showModal: {
      type: Boolean,
      default: false,
    },

    optionType: String,

    // Optional cap on the length of the joined selection (as emitted by OK).
    maxLength: {
      type: Number,
      default: null,
    },

    existingOptions: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      isLoading: false,
      pickedOptionIds: [],
      searchQuery: '',
      isMaxReached: false,
    }
  },

  computed: {
    ...mapState({
      options: (state) => state.optionValue.list,
    }),

    pickedJoined() {
      return this.joinPicked()
    },

    isOverMaxLength() {
      return !!this.maxLength && this.pickedJoined.length > this.maxLength
    },

    show: {
      get() {
        return this.showModal
      },
      set() {
        this.resetData()
        this.$emit('onCloseOptionsPickerModal')
      },
    },
  },

  methods: {
    ...mapActions({
      getOptionValues: 'optionValue/getOptionValues',
    }),

    async onShow() {
      this.isLoading = true

      try {
        await this.getOptionValues(this.optionType)
        this.resetData()
      } catch (e) {
        this.$bvToast.toast('Could not load options. Please try again.', {
          variant: 'danger',
          title: 'Error',
        })
      } finally {
        this.isLoading = false
      }
    },

    // prettier-ignore
    sortOptions() {
      // Sort order_no, then by checked status
      this.options.sort((a, b) => (a.order_no < b.order_no ? -1 : 1))
      this.options.sort((a, b) => this.pickedOptionIds.includes(b.id) - this.pickedOptionIds.includes(a.id))
    },

    isChecked(id) {
      return this.pickedOptionIds.includes(id)
    },

    onSearchQueryChange() {
      this.sortOptions()
    },

    onCheckChange() {
      this.isMaxReached = false

      if (this.pickedOptionIds.length > 999) {
        this.isMaxReached = true
        this.pickedOptionIds.pop()
      }
    },

    // Same string onOk emits; sorted on a copy so computed access never
    // reorders the shared list.
    joinPicked() {
      return [...this.options]
        .sort((a, b) => (a.value < b.value ? -1 : 1))
        .filter((option) => this.pickedOptionIds.includes(option.id))
        .map((option) =>
          ['high_risk_work_class', 'driver_licence_class'].includes(
            this.optionType,
          )
            ? option.value.split(' ')[0]
            : option.value,
        )
        .join(', ')
    },

    onOk() {
      this.$emit('onOptionsPicked', {
        options: this.joinPicked(),
      })
    },

    resetData() {
      // Generate picked option ids
      this.pickedOptionIds = this.existingOptions
        ? this.existingOptions
            .split(',')
            .map((optionValue) => {
              // prettier-ignore
              const option =  ['high_risk_work_class', 'driver_licence_class'].includes(this.optionType)
                ? this.options.find((o) => o.value.split(' ')[0].trim() === optionValue.trim())
                : this.options.find((o) => o.value.trim() === optionValue.trim())
              return option ? option.id : null
            })
            .filter((id) => id !== null)
        : []

      this.sortOptions()
      this.searchQuery = ''
      this.isMaxReached = false
    },
  },
}
</script>
